import { AxiosResponse } from 'axios';

import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from 'utils/http';
import {
  IDeleteDriverShiftsFilters,
  IUpdateDriverShiftRequest,
  IUdateDriverShiftResponse,
  IGetSignleDriverShiftResponse,
  IGetDriverShiftsResponse,
  IGetDriverShiftsFilters,
  IDeleteDriverShiftResponse,
  ICreateMultipleDriverShiftsResponse,
  IGetDriverShiftMonthlyResponse,
  IGetDriverShiftWeeklyResponse,
  IGetDriverShiftsWeeklyFilters,
  IGetDriverShiftsDailyFilters,
  IGetDriverShiftDailyResponse,
  IDriverShiftsQueryRequestMonthlyFilters,
  ICreateDriverShiftRequest,
} from 'models/http/driverShift';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { HTTP } from './core.http';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';
import DataUploadBaseHttp from './utils/data-upload.http';
import { IGetHistoryResponse } from 'models/http/history';

class DriverShiftHttp extends DataUploadBaseHttp implements HTTP<IDeleteDriverShiftsFilters> {
  public static DataExportRoute = `${ModelsNames.DriverShifts}/data-export`;
  protected readonly route: DataUploadModels = ModelsNames.DriverShifts;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async get(filters?: IGetDriverShiftsFilters, abortController?: AbortController): Promise<IGetDriverShiftsResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftsResponse>) => {
        return response.data;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSignleDriverShiftResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSignleDriverShiftResponse>) => {
        return response.data;
      });
  }

  public async getDay(filters: IGetDriverShiftsDailyFilters, abortController?: AbortController): Promise<IGetDriverShiftDailyResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}/calendar/daily?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftDailyResponse>) => {
        return response.data;
      });
  }

  public async getWeek(filters: IGetDriverShiftsWeeklyFilters, abortController?: AbortController): Promise<IGetDriverShiftWeeklyResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`${this.route}/calendar/weekly?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftWeeklyResponse>) => {
        return response.data;
      });
  }

  public async getMonth(
    filters: IDriverShiftsQueryRequestMonthlyFilters,
    abortController?: AbortController,
  ): Promise<IGetDriverShiftMonthlyResponse> {
    const query = prepareQPFromFilters(filters);

    return serverAxios
      .get(`/${this.route}/calendar/monthly?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftMonthlyResponse>) => {
        return response.data;
      });
  }

  public async createMany(data: ICreateDriverShiftRequest): Promise<ICreateMultipleDriverShiftsResponse> {
    return serverAxios
      .post(`/${this.route}`, {
        ...data,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateMultipleDriverShiftsResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, data: IUpdateDriverShiftRequest): Promise<IUdateDriverShiftResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUdateDriverShiftResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteDriverShiftResponse>;
  public async delete(filters: IDeleteDriverShiftsFilters): Promise<IDeleteDriverShiftResponse>;
  public async delete(filters: number[] | IDeleteDriverShiftsFilters): Promise<IDeleteDriverShiftResponse> {
    return serverAxios
      .delete(`/${this.route}/`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteDriverShiftResponse>) => {
        return response.data;
      });
  }
}

export default DriverShiftHttp;
